/**
 * The signal that tells this browser's other tabs a tab has logged out (P1.4-T7, ADR-075 decision 6). It
 * carries no token and names no account: a tab that hears it asks the gateway about its own session, and only
 * a refusal ends that tab's sign-in, so a logout of one account never ends another account's tab. The page
 * sends and listens on one channel object, which a browser never delivers its own messages to, so a tab never
 * hears its own logout.
 */

/**
 * The channel's name.
 */
export const AUTH_CHANNEL = "odyssey.auth";

/**
 * The one message the channel carries.
 */
const LOGGED_OUT = { kind: "logged-out" } as const;

/**
 * Where the signal goes and comes from: the browser's `BroadcastChannel`, or a stand-in a test gives.
 */
export interface ChannelPort {
  readonly postMessage: (message: unknown) => void;
  readonly addEventListener: (type: "message", listener: (event: MessageEvent) => void) => void;
  readonly removeEventListener: (type: "message", listener: (event: MessageEvent) => void) => void;
}

let opened: ChannelPort | null | undefined;

/**
 * The page's one channel, opened on first use.
 *
 * @returns The channel, or null where the browser has no `BroadcastChannel`, in which case no signal is sent
 *   or heard and a logged-out tab learns of it at its next refresh.
 */
function pageChannel(): ChannelPort | null {
  opened ??= typeof BroadcastChannel === "function" ? new BroadcastChannel(AUTH_CHANNEL) : null;
  return opened;
}

/**
 * Tells the other tabs that this one has logged out, or logged out everywhere.
 *
 * @param port The channel, the page's unless a test gives another, or null for none.
 */
export function announceLogout(port: ChannelPort | null = pageChannel()): void {
  port?.postMessage(LOGGED_OUT);
}

/**
 * Calls back whenever another tab says it has logged out. A message of any other shape is ignored.
 *
 * @param onLoggedOut What to do: the page checks its own session with the gateway.
 * @param port The channel, the page's unless a test gives another, or null for none.
 * @returns A function that stops listening.
 */
export function onLogoutElsewhere(onLoggedOut: () => void, port: ChannelPort | null = pageChannel()): () => void {
  if (port === null) {
    return () => undefined;
  }
  const listener = (event: MessageEvent): void => {
    const data: unknown = event.data;
    if (typeof data === "object" && data !== null && (data as { kind?: unknown }).kind === LOGGED_OUT.kind) {
      onLoggedOut();
    }
  };
  port.addEventListener("message", listener);
  return () => port.removeEventListener("message", listener);
}
